export interface AnsiSpan {
  text: string;
  className: string;
  color?: string;
  background?: string;
}

/** A palette index (0-15), or an rgb() for the 256-colour and truecolour escapes. */
type Color = number | string | null;

interface Pen {
  fg: Color;
  bg: Color;
  bold: boolean;
  dim: boolean;
  italic: boolean;
  underline: boolean;
  inverse: boolean;
  strike: boolean;
}

const BLANK: Pen = {
  fg: null,
  bg: null,
  bold: false,
  dim: false,
  italic: false,
  underline: false,
  inverse: false,
  strike: false,
};

// Built from char codes: a control character written into a regex literal is what no-control-regex exists to catch.
const ESC = String.fromCharCode(27);
const BEL = String.fromCharCode(7);
const control = (from: number, to: number) =>
  `${String.fromCharCode(from)}-${String.fromCharCode(to)}`;

// CSI (colours, cursor moves), OSC (window titles), charset picks, and any other two-byte escape.
const SEQUENCE = new RegExp(
  `${ESC}(?:\\[([0-9;:?]*)[ -/]*([@-~])|\\][^${BEL}${ESC}]*(?:${BEL}|${ESC}\\\\)?|[()#][0-9A-Za-z]|[\\s\\S])`,
  'g',
);
// Everything below space except tab, newline and the escape itself.
const STRAY_CONTROLS = new RegExp(
  `[${control(0, 8)}${control(11, 12)}${control(14, 26)}${control(28, 31)}]`,
  'g',
);

const CUBE_LEVEL = (value: number) => (value ? 55 + value * 40 : 0);

const color256 = (index: number): Color => {
  if (index < 16) return index;
  if (index > 231) {
    const gray = 8 + (index - 232) * 10;
    return `rgb(${gray}, ${gray}, ${gray})`;
  }
  const cube = index - 16;
  return `rgb(${CUBE_LEVEL(Math.floor(cube / 36))}, ${CUBE_LEVEL(Math.floor(cube / 6) % 6)}, ${CUBE_LEVEL(cube % 6)})`;
};

/** `38;5;N` and `38;2;R;G;B`: returns the colour and how many extra codes it used. */
const extendedColor = (codes: number[], at: number): [Color, number] => {
  if (codes[at + 1] === 5) return [color256(codes[at + 2] ?? 0), 2];
  if (codes[at + 1] === 2)
    return [
      `rgb(${codes[at + 2] ?? 0}, ${codes[at + 3] ?? 0}, ${codes[at + 4] ?? 0})`,
      4,
    ];
  return [null, 0];
};

const ATTRIBUTES: Record<number, Partial<Pen>> = {
  0: BLANK,
  1: { bold: true },
  2: { dim: true },
  3: { italic: true },
  4: { underline: true },
  7: { inverse: true },
  9: { strike: true },
  22: { bold: false, dim: false },
  23: { italic: false },
  24: { underline: false },
  27: { inverse: false },
  29: { strike: false },
  39: { fg: null },
  49: { bg: null },
};

// 30-37 and 40-47 are the first eight colours, 90-97 and 100-107 their bright twins.
const paletteColor = (code: number): Partial<Pen> => {
  const band = Math.floor(code / 10);
  const index = (code % 10) + (band >= 9 ? 8 : 0);
  if (code % 10 > 7) return {};
  if (band === 3 || band === 9) return { fg: index };
  if (band === 4 || band === 10) return { bg: index };
  return {};
};

const applySgr = (start: Pen, params: string): Pen => {
  const codes = (params || '0').split(/[;:]/).map(code => Number(code) || 0);
  const pen = { ...start };
  for (let at = 0; at < codes.length; at++) {
    const code = codes[at];
    if (code === 38 || code === 48) {
      const [color, used] = extendedColor(codes, at);
      Object.assign(pen, code === 38 ? { fg: color } : { bg: color });
      at += used;
    } else {
      Object.assign(pen, ATTRIBUTES[code] ?? paletteColor(code));
    }
  }
  return pen;
};

const paint = (text: string, pen: Pen): AnsiSpan => {
  let fg = pen.inverse ? pen.bg : pen.fg;
  const bg = pen.inverse ? pen.fg : pen.bg;
  // iTerm2's "brighten bold text": bold in one of the first eight colours draws in its bright twin.
  if (pen.bold && typeof fg === 'number' && fg < 8) fg += 8;

  const roles = [
    pen.inverse && 'inverse',
    typeof fg === 'number' && `fg-${fg}`,
    typeof bg === 'number' && `bg-${bg}`,
    pen.bold && 'bold',
    pen.dim && 'dim',
    pen.italic && 'italic',
    pen.underline && 'underline',
    pen.strike && 'strike',
  ].filter(Boolean);

  return {
    text,
    className: [
      'CliTerminal__ansi',
      ...roles.map(role => `CliTerminal__ansi--${role}`),
    ].join(' '),
    color: typeof fg === 'string' ? fg : undefined,
    background: typeof bg === 'string' ? bg : undefined,
  };
};

/** A bare `\r` sends the cursor back to the start of the line, so a progress bar keeps only its last frame. */
const settleCarriageReturns = (text: string) =>
  text
    .split('\r\n')
    .join('\n')
    .split('\n')
    .map(line => {
      // A trailing \r is usually the first half of a \r\n still in flight.
      const body = line.endsWith('\r') ? line.slice(0, -1) : line;
      return body.slice(body.lastIndexOf('\r') + 1);
    })
    .join('\n');

export const hasTerminalControls = (text: string) =>
  text.includes(ESC) || text.includes('\r');

/** What a TTY printed, as styled spans: colours and weights are kept, every other control sequence is dropped. */
export const parseAnsi = (raw: string): AnsiSpan[] => {
  const text = settleCarriageReturns(raw);
  const spans: AnsiSpan[] = [];
  let pen = BLANK;
  let from = 0;
  // Stripped per run, not up front: BEL is also what ends a window-title sequence.
  const emit = (to: number) => {
    const chunk = text.slice(from, to).replace(STRAY_CONTROLS, '');
    if (chunk) spans.push(paint(chunk, pen));
  };

  SEQUENCE.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = SEQUENCE.exec(text))) {
    emit(match.index);
    from = match.index + match[0].length;
    if (match[2] === 'm') pen = applySgr(pen, match[1]);
  }
  emit(text.length);
  return spans;
};
